import _axios from 'axios';
import queryString from 'query-string';
import store from '../redux/store';
import { msalInstance, MS_SCOPES_API } from '../config/msal';
import { validateUserFailed, refreshMicrosoftSession } from '../redux/authentication/slice';
import renderNotification from '../helpers/notifications/renderNotification';
import { getStatusText } from '../helpers/httpStatus';
import { sentryCaptureException } from '../config/sentry';
import { NOTIFICATION_MESSAGES } from '../localization/Notifications/dictionary';
import { BASE_URL } from './base';

export const AXIOS_CANCEL_CODE = 'ERR_CANCELED';

export const DEFAULT_TIMEOUT = 60000; // 60 seconds
export const LONG_TIMEOUT = 600000; // 10 minutes

// This function is used to check if an error is ignorable, meaning it should not trigger a notification inside components
// we already handle these errors in the axios interceptor
// TODO: on cancel there should not be an error in states, we need to fix that sometimes
const isIgnorableError = (error) => error?.code === AXIOS_CANCEL_CODE || error?.response?.status === 401;

// PII-safe request context for Sentry: param keys only, never values.
const getRequestContext = (error) => {
  const config = error?.config;
  if (!config) {
    return undefined;
  }

  const status = error?.response?.status;
  let paramKeys;
  try {
    paramKeys = config.params ? Object.keys(config.params) : undefined;
  } catch {
    paramKeys = undefined;
  }

  return {
    method: (config.method || '').toUpperCase(),
    url: config.url,
    status,
    status_text: status ? getStatusText(status) : undefined,
    param_keys: paramKeys
  };
};

const axios = _axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  paramsSerializer: (params) => queryString.stringify(params),
  withCredentials: true
});

axios.interceptors.request.use(
  async (config) => {
    try {
      // Check if the user has an active microsoft account login and if so, acquire a new token silently before each request.
      const activeMicrosoftAccount = msalInstance.getActiveAccount();

      if (activeMicrosoftAccount) {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: [MS_SCOPES_API],
          account: activeMicrosoftAccount
        });
        if (tokenResponse?.accessToken && !tokenResponse?.fromCache) {
          // If the token was aquired from the network (not from the cache), it means that there is a new token we need to send to the backend in order to refresh the session.
          store.dispatch(refreshMicrosoftSession({ accessToken: tokenResponse.accessToken }));
        }
      }
    } catch {
      // If the token acquisition fails for any reason, we continue with the api call normally.
      // The missing authentication will trigger a unauthorized response which will then redirect the user to the login screen.
    }

    return config;
  },
  (error) => {
    // If an error occurs on request we show the user a general error message and log the error
    renderNotification({
      type: 'error',
      message: NOTIFICATION_MESSAGES.general_error_message
    });
    // eslint-disable-next-line no-console
    console.error(`Error while making a request: ${error.message}`);
    sentryCaptureException(new Error(`Error while making a request: ${error.message}`), getRequestContext(error));
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // The errors handeled in the following block are expected/desired behaviour, they should not display an error notification
    if (isIgnorableError(error)) {
      // If we receive an unauthorized error, we trigger a validate user error action
      // as this will logout the user and redirect to the login screen
      if (error?.response?.status === 401) {
        store.dispatch(validateUserFailed());
      }

      return Promise.reject(error);
    }

    // Some requests handle and display their own errors (e.g. a dedicated inline banner) and opt out of the
    // generic notification via this per-request axios config flag, set on the call in the relevant api.js file.
    const suppressNotification = error?.config?.suppressErrorNotification === true;

    const response = error?.response;

    if (response?.data?.message) {
      /*
      This if block handels "controlled" errors where we get a response with a message from our backend.
      These errors are either valid cases where we want to show the user a message or technical errors that were handeled (logged to sentry) by the backend.
      For the valid cases we display a notification with the backend message and for technical errors (e.g. 500 - Internal Server Error)
      we display a generic error notification and log the technical error message to the console for the developers to be able to debug it.
      */
      const errorMessage = response.data?.message;

      if (response.status === 500) {
        if (!suppressNotification) {
          renderNotification({
            type: 'error',
            message: NOTIFICATION_MESSAGES.general_error_message
          });
        }
        // eslint-disable-next-line no-console
        console.error('Internal Server Error:', errorMessage);
      } else if (!suppressNotification) {
        renderNotification({
          type: 'error',
          message: errorMessage
        });
      }

      return Promise.reject(error);
    } else {
      /*
      This else block handeles "uncontrolled" errors where we either do not get a response or we do get a response without a message.
      In this case we need to send the error to sentry and display a generic error notification to the user.
      */
      const errorMessage = !response
        ? `No response for the request. Error Message: ${error.message}`
        : `${error.message} (${getStatusText(response?.status)})`;

      if (!suppressNotification) {
        renderNotification({
          type: 'error',
          message: NOTIFICATION_MESSAGES.general_error_message
        });
      }

      sentryCaptureException(new Error(errorMessage), getRequestContext(error));
      return Promise.reject(error);
    }
  }
);

export default axios;
